import { Close } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  addCategoryApi,
  updateCategoryApi,
} from "../../../services/category.service";
import { toast } from "react-toastify";
import CategorySchema from "../../../joi/master/category.schema";

export default function CategoryModal({
  open,
  onClose,
  refetch,
  selectedRow = null,
}) {
  const initialState = {
    categoryName: "",
    code: "",
    isClaimRateAllowed: false,
    isSaleRate: false,
    saleRateBasedOn: {
      option: "",
      percentage: 0,
    },
    limitSaleRateToMaximumToMRP: false,
    limitSaleRateToMinimumToPurchaseRate: false,
    limitSaleRateBetweenMRPAndPurchaseRate: false,
    applyCondition: false,
    active: true,
    allowDecimalInQty: false,
    isProviderMandatory: false,
  };

  const [formData, setFormData] = useState(initialState);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevValue) => {
      // Handle nested saleRateBasedOn fields
      if (name === "option" || name === "percentage") {
        return {
          ...prevValue,
          saleRateBasedOn: {
            ...prevValue.saleRateBasedOn,
            [name]: type === "number" ? parseFloat(value) || 0 : value,
          },
        };
      }

      return {
        ...prevValue,
        [name]: type === "checkbox" ? checked : value,
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let response;

    // Prepare the data to submit
    const submitData = { ...formData };
    
    // Only include saleRateBasedOn if isSaleRate is true
    if (!submitData.isSaleRate) {
      delete submitData.saleRateBasedOn;
    }

    const { error } = CategorySchema.validate(submitData);

    if (error) {
      error.details.forEach((err) => toast.error(err.message));
      return;
    }

    setIsLoading(true);
    try {
      if (selectedRow?._id) {
        response = await updateCategoryApi(submitData);
      } else {
        response = await addCategoryApi(submitData);
      }
      
      if (response?.isSuccess || response?.code === 200) {
        toast.success(
          response?.message ||
            `Category ${selectedRow?._id ? "updated" : "added"} successfully`
        );
        setFormData(initialState);
        onClose();
        // Add a small delay to ensure the modal closes before refetching
        setTimeout(() => {
          refetch();
        }, 100);
      } else {
        toast.error(response?.message || "Some error occurred.");
      }
    } catch (error) {
      console.error("Category submission error:", error);
      toast.error("An unexpected error occurred.");
    } finally {
      setIsLoading(false);
    }
  };

  // Sync selectedRow data when modal opens
  useEffect(() => {
    if (selectedRow?._id) {
      setFormData({
        ...initialState,
        _id: selectedRow._id,
        categoryName: selectedRow?.categoryName || "",
        code: selectedRow?.code || "",
        isClaimRateAllowed: selectedRow?.isClaimRateAllowed ?? false,
        isSaleRate: selectedRow?.isSaleRate ?? false,
        saleRateBasedOn: {
          option: selectedRow?.saleRateBasedOn?.option || "",
          percentage: selectedRow?.saleRateBasedOn?.percentage ?? 0,
        },
        limitSaleRateToMaximumToMRP:
          selectedRow?.limitSaleRateToMaximumToMRP ?? false,
        limitSaleRateToMinimumToPurchaseRate:
          selectedRow?.limitSaleRateToMinimumToPurchaseRate ?? false,
        limitSaleRateBetweenMRPAndPurchaseRate:
          selectedRow?.limitSaleRateBetweenMRPAndPurchaseRate ?? false,
        applyCondition: selectedRow?.applyCondition ?? false,
        active: selectedRow?.active ?? true,
        allowDecimalInQty: selectedRow?.allowDecimalInQty ?? false,
        isProviderMandatory: selectedRow?.isProviderMandatory ?? false,
      });
    } else {
      setFormData(initialState);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            maxWidth: "unset",
            width: { xs: "95vw", lg: "1100px" },
            bgcolor: "#E5F4FF",
            borderRadius: "20px",
          },
        },
      }}
    >
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: "20px",
          }}
        >
          <Typography sx={{ color: "rgba(93, 106, 130, 1)", fontSize: 20 }}>
            {selectedRow?._id ? `Edit Category` : `Add Category`}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <TextField
                label="Category Name"
                name="categoryName"
                required
                fullWidth
                value={formData?.categoryName || ""}
                onChange={handleChange}
                sx={{ bgcolor: "white" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <TextField
                label="Code"
                name="code"
                required
                fullWidth
                value={formData?.code || ""}
                onChange={handleChange}
                sx={{ bgcolor: "white" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="active"
                    checked={formData?.active}
                    onChange={handleChange}
                  />
                }
                label="Active"
              />
            </Grid>

            {/* active */}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="allowDecimalInQty"
                    checked={formData?.allowDecimalInQty}
                    onChange={handleChange}
                  />
                }
                label="Allow Decimal In Qty."
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="isClaimRateAllowed"
                    checked={formData?.isClaimRateAllowed}
                    onChange={handleChange}
                  />
                }
                label="Is Claim Rate Allowed?"
              />
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  border: `1px solid rgba(204,212,218,1)`,
                  borderRadius: "12px",
                  py: 1,
                  px: 2,
                }}
              >
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="isSaleRate"
                          checked={formData?.isSaleRate}
                          onChange={handleChange}
                        />
                      }
                      label="Is Sale Rate?"
                    />
                  </Grid>
                  {formData?.isSaleRate && (
                    <>
                      <Grid item xs={12}>
                        <Typography>Sale Rate Based On*</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Option"
                          name="option"
                          fullWidth
                          value={formData?.saleRateBasedOn?.option || ""}
                          onChange={handleChange}
                          sx={{ bgcolor: "white" }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Percentage"
                          name="percentage"
                          type="number"
                          fullWidth
                          value={formData?.saleRateBasedOn?.percentage || ""}
                          onChange={handleChange}
                          sx={{ bgcolor: "white" }}
                          slotProps={{
                            input: {
                              endAdornment: (
                                <InputAdornment position="end">%</InputAdornment>
                              ),
                            },
                          }}
                        />
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="limitSaleRateToMaximumToMRP"
                          checked={formData?.limitSaleRateToMaximumToMRP}
                          onChange={handleChange}
                        />
                      }
                      label="Limit Sale Rate To Maximum To MRP"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="limitSaleRateToMinimumToPurchaseRate"
                          checked={
                            formData?.limitSaleRateToMinimumToPurchaseRate
                          }
                          onChange={handleChange}
                        />
                      }
                      label="Limit Sale Rate To Minimum To Purchase Rate"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="limitSaleRateBetweenMRPAndPurchaseRate"
                          checked={
                            formData?.limitSaleRateBetweenMRPAndPurchaseRate
                          }
                          onChange={handleChange}
                        />
                      }
                      label="Limit Sale Rate Between MRP And Purchase Rate"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="applyCondition"
                          checked={formData?.applyCondition}
                          onChange={handleChange}
                        />
                      }
                      label="Apply Condition"
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ display: "flex", justifyContent: "center", mt: "20px" }}>
            <Button
              variant="contained"
              type="submit"
              sx={{
                textTransform: "none",
                backgroundColor: "rgba(1, 121, 176, 1)",
                width: "150px",
              }}
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={24} />
              ) : selectedRow?._id ? (
                "Update"
              ) : (
                `Submit`
              )}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
